import { Observable } from 'rxjs';

export interface PackageSubscriptionPayload {
  subscriptions: {
    plan: SubscriptionPlan;
    id: string;
    originalBundleInvoice?: string;
  }[];
  companyId?: string;
  isSimulation?: boolean;
}

export interface FlatRatePackageSubscriptionPayload {
  subscriptions: [
    {
      plan: SubscriptionPlan.Pro;
    }
  ];
  isSimulation?: boolean;
}

export interface CommercialPackageSubscriptionPayload {
  subscriptions: [
    {
      plan: SubscriptionPlan;
    }
  ];
  isSimulation?: boolean;
}

export interface PaidFeaturesResponse {
  devices: SubscriptionWithPaidFeatures[];
}

export interface SubscriptionWithPaidFeatures {
  resourceId: string;
  plan: SubscriptionPlan;
  features: PaidFeature[];
  scope: PaidFeatureScope;
}

export enum PaidFeatureScope {
  company = 'company',
  device = 'device',
}

export enum SubscriptionPlan {
  Plus = 'Plus',
  Pro = 'Pro',
  Trial = 'Trial',
  Basic = 'Basic',
  Free = 'Free',
}

export enum SubscriptionStatus {
  active = 'active',
  canceled = 'cancelled',
  expired = 'expired',
  future = 'future',
  learning = 'Learning',
}

export enum CancelSubscriptionReasonType {
  PERFORMANCE = 'PERFORMANCE',
  MISSING_FEATURES = 'MISSING_FEATURES',
  NO_ADDED_VALUE = 'NO_ADDED_VALUE',
  NOT_SURE_HOW_TO_USE = 'NOT_SURE_HOW_TO_USE',
  CANCELLED_CONTRACT = 'CANCELLED_CONTRACT',
  NOT_MEET_EXPECTATION = 'NOT_MEET_EXPECTATION',
  OTHER = 'OTHER',
}

export interface Subscription {
  boilerId: string;
  expirationDate: Date;
  hardwareBundle?: HardwareBundle;
  id: number;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  subscribedBy: string;
  subscribedByName: string;
  subscriptionDate: Date;
  trial: boolean;
}

export interface TrialSubscription {
  id: number;
  createdBy: string;
  deviceId: string;
  createdAt: Date;
  expirationDate: Date;
  status: SubscriptionStatus;
}

export interface TrialSubscriptionsList {
  total: number;
  remaining: number;
  createdTrialVersions: TrialSubscription[];
  period: {
    months: number;
  };
}

export interface BookingSubscriptionsPlan {
  name: SubscriptionPlan;
  price: {
    currency: string;
    price: number;
    basePrice?: number;
  };
  features: BookingSubscriptionsPlanFeature[];
}
export interface BookingSubscriptionsPlanFeature {
  name: string;
  enabled: boolean;
}

export interface UpdateBookingSubscriptionResponse {
  CUSTOMER: string;
  SALES_ORG: string;
  DISTR_CHAN: string;
  DIVISION: string;
  EQUIPMENT: string;
  MATERIAL: number;
  START_DATE: string;
  NAME: string;
  VBELN: number;
}

export interface SubscriptionResult {
  CUSTOMER: string;
  SALES_ORG: string;
  DISTR_CHAN: string;
  DIVISION: string;
  EQUIPMENT: string;
  MATERIAL: number;
  UUID: string;
}

export interface SubscriptionsPriceDetails {
  currency: string;
  net: number;
  taxRate: number;
  tax: number;
  total: number;
}

export interface SubscriptionItems {
  name: string;
  pos: number;
  price: number;
  quantity: number;
}

export interface SubscriptionsDetails {
  items: SubscriptionItems[];
  price: SubscriptionsPriceDetails;
  result: SubscriptionResult[];
}

export interface ContingentSubscription {
  invoice: string;
  invoiceDate: Date;
  companyNumber: string;
  quantity: number;
  used: number;
  free: number;
}

export interface CancelSubscriptionReasons {
  reasonKey: string;
  name: string;
  additionalCommentsMandatory: boolean;
}

export interface HardwareBundle {
  orderNumber: string;
  partnerShopUrl: string;
  invoiceId: string;
}

export interface SupportedFeatures {
  deviceId: string;
  features: [
    {
      name: SupportedFeaturesName;
      supported: boolean;
    }
  ];
}

export interface VoucherValidationDetails {
  valid: boolean;
  reasons: string[];
}

export enum SupportedFeaturesName {
  permamentInspection = 'PermanentInspection',
}

export interface InstallationMonetizationFeatures {
  installations: {
    monetizationFeatures: MonetizationFeature[];
    gateways: GatewayMonetizationFeatures[];
    installationId: number;
  }[];
}

export interface GatewayMonetizationFeatures {
  monetizationFeatures: MonetizationFeature[];
  gatewaySerial: string;
  devices: DeviceMonetizationFeatures[];
}

export interface DeviceMonetizationFeatures {
  deviceId: string;
  monetizationFeatures: MonetizationFeature[];
}

export interface MonetizationFeature {
  name: string;
  reason: string;
  supported: boolean;
}

export interface MonetizationApiModel {
  getVitoguideSubscriptions(companyId?: string): Observable<Subscription[]>;
}

export enum PaidFeature {
  Analytics = 'Analytics',
  AutomatedRecommendations = 'AutomatedRecommendations',
  Dashboard = 'Dashboard',
  DataRecorderAndDataTrending = 'DataRecorderAndDataTrending',
  Equipment = 'Equipment',
  EventHistory = 'EventHistory',
  ExtendedParameters = 'ExtendedParameters',
  Features = 'Features',
  GuidedTroubleshooting = 'GuidedTroubleshooting',
  HEMS = 'HEMS',
  LiveUpdates = 'LiveUpdates',
  Mails = 'Mails',
  Monetization = 'Monetization',
  ParameterComparison = 'ParameterComparison',
  PermanentInspection = 'PermanentInspection',
  PredictiveMaintenance = 'PredictiveMaintenance',
  Reports = 'Reports',
  SmartNotes = 'SmartNotes',
  SystemOverview = 'SystemOverview',
  UserManagement = 'UserManagement',
  ViCareDefaultAccess = 'ViCareDefaultAccess',
  ViguideDefaultAccess = 'ViguideDefaultAccess',
}
